import { useEffect } from 'react';
import { useBusinessStore } from '../store/businessStore';
import { apiService } from '../services/api';

/**
 * Custom hook to automatically fetch and sync users and departments
 * Use this in any component that needs access to business users/departments
 */
export const useBusinessData = (options?: { 
  fetchUsers?: boolean; 
  fetchDepartments?: boolean;
  autoRefresh?: boolean;
}) => {
  const {
    users,
    departments,
    isLoadingUsers,
    isLoadingDepartments,
    setUsers,
    setDepartments,
    setIsLoadingUsers,
    setIsLoadingDepartments,
    markUsersFetchAttempted,
    markDepartmentsFetchAttempted,
    shouldRefreshUsers,
    shouldRefreshDepartments,
    getUserById,
    getDepartmentById
  } = useBusinessStore();

  const { 
    fetchUsers = true, 
    fetchDepartments = true,
    autoRefresh = true 
  } = options || {};

  // Fetch users
  useEffect(() => {
    if (!fetchUsers) return;
    
    const loadUsers = async () => {
      // Skip if already loading or cache is fresh
      if (isLoadingUsers || (!autoRefresh && users.length > 0) || !shouldRefreshUsers()) {
        return;
      }

      try {
        setIsLoadingUsers(true);
        const businessId = apiService.getBusinessIdFromToken();
        if (!businessId) {
          console.error('No business ID found');
          return;
        }
        const response = await apiService.authenticatedRequest(`/api/business/${businessId}/users`, {
          method: 'GET'
        });

        if (response.statusCode === 1000 && response.data && Array.isArray(response.data)) {
          const allUsers = response.data.map((bu: any) => ({
            _id: bu.user?._id || bu.userId,
            firstName: bu.user?.firstName || '',
            lastName: bu.user?.lastName || '',
            email: bu.user?.email || '',
            role: bu.role || '',
            isActive: bu.isActive !== false
          }));
          setUsers(allUsers);
        } else {
          // Non-1000 response doesn't throw, so without this
          // shouldRefreshUsers() would stay true forever and this effect
          // (which re-runs on every isLoadingUsers flip) would refetch on
          // every render — see markUsersFetchAttempted's doc comment.
          markUsersFetchAttempted();
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        markUsersFetchAttempted();
      } finally {
        setIsLoadingUsers(false);
      }
    };

    loadUsers();
  }, [fetchUsers, autoRefresh, isLoadingUsers, users.length, shouldRefreshUsers, setIsLoadingUsers, setUsers, markUsersFetchAttempted]);

  // Fetch departments
  useEffect(() => {
    if (!fetchDepartments) return;

    const loadDepartments = async () => {
      // Skip if already loading or cache is fresh
      if (isLoadingDepartments || (!autoRefresh && departments.length > 0) || !shouldRefreshDepartments()) {
        return;
      }

      try {
        setIsLoadingDepartments(true);
        const businessId = apiService.getBusinessIdFromToken();
        if (!businessId) {
          console.error('No business ID found');
          return;
        }
        const response = await apiService.authenticatedRequest(`/api/departments/business/${businessId}`, {
          method: 'GET'
        });

        if (response.statusCode === 1000 && response.data && Array.isArray(response.data)) {
          const activeDepartments = response.data.filter((d: any) => d.isActive !== false);
          setDepartments(activeDepartments);
        } else {
          markDepartmentsFetchAttempted();
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
        markDepartmentsFetchAttempted();
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    loadDepartments();
  }, [fetchDepartments, autoRefresh, isLoadingDepartments, departments.length, shouldRefreshDepartments, setIsLoadingDepartments, setDepartments, markDepartmentsFetchAttempted]);

  return {
    users,
    departments,
    isLoadingUsers,
    isLoadingDepartments,
    getUserById,
    getDepartmentById,
    // Helper to manually refresh
    refreshUsers: async () => {
      setIsLoadingUsers(true);
      try {
        const businessId = apiService.getBusinessIdFromToken();
        if (!businessId) {
          console.error('No business ID found');
          return;
        }
        const response = await apiService.authenticatedRequest(`/api/business/${businessId}/users`, {
          method: 'GET'
        });
        if (response.statusCode === 1000 && response.data && Array.isArray(response.data)) {
          const allUsers = response.data.map((bu: any) => ({
            _id: bu.user?._id || bu.userId,
            firstName: bu.user?.firstName || '',
            lastName: bu.user?.lastName || '',
            email: bu.user?.email || '',
            role: bu.role || '',
            isActive: bu.isActive !== false
          }));
          setUsers(allUsers);
        } else {
          markUsersFetchAttempted();
        }
      } catch (error) {
        console.error('Error refreshing users:', error);
        markUsersFetchAttempted();
      } finally {
        setIsLoadingUsers(false);
      }
    },
    refreshDepartments: async () => {
      setIsLoadingDepartments(true);
      try {
        const businessId = apiService.getBusinessIdFromToken();
        if (!businessId) {
          console.error('No business ID found');
          return;
        }
        const response = await apiService.authenticatedRequest(`/api/departments/business/${businessId}`, {
          method: 'GET'
        });
        if (response.statusCode === 1000 && response.data && Array.isArray(response.data)) {
          const activeDepartments = response.data.filter((d: any) => d.isActive !== false);
          setDepartments(activeDepartments);
        } else {
          markDepartmentsFetchAttempted();
        }
      } catch (error) {
        console.error('Error refreshing departments:', error);
        markDepartmentsFetchAttempted();
      } finally {
        setIsLoadingDepartments(false);
      }
    }
  };
};
